// Hisobotlar — `xsib-report` mikroservisi bilan kontrakt.
//
// ⚠️ SHAKLLAR ENDI TAXMIN EMAS. Bu fayl ilgari "javob shakli tekshirilmagan"
// deb yozilgan va sxemalari yumshoq (`z.unknown()`, `.nullish()`) edi. Har
// bir endpoint jonli tizimda (https://api.x-sib.uz, `af-isp` va `uz-venue`)
// chaqirib ko'rildi va shakl backend DTO'lari bilan solishtirildi:
//
//   GET /api/reports/dashboard              → DashboardSummary
//   GET /api/reports/traffic                → TrafficPoint[]
//   GET /api/reports/traffic/{subscriberId} → TrafficPoint[]
//   GET /api/reports/top-consumers          → TopConsumer[]
//   GET /api/reports/revenue                → RevenuePoint[]
//   GET /api/reports/agents                 → AgentPerformance[]
//
// Hammasi `report.read` huquqi ostida (`@RequiresPermission(REPORT_READ)`).
//
// ⚠️ ESKI IZOHDAGI IKKI XATO TUZATILDI:
//
//   1. `from`/`to` — `LocalDate` (`YYYY-MM-DD`), ISO-8601 `Instant` EMAS.
//      Ilgari bu yerda `dayjs().toISOString()` yuborilardi va server
//      400 qaytarardi: `Parse attempt failed for value [2026-08-05T00:00…Z]`.
//      Sabab kontraktda ochiq yozilgan (`UsageResource` javadoc'i): "kun"
//      tushunchasi zonasiz mavjud emas, va u zona brauzerniki emas —
//      TENANTNIKI. Shuning uchun mijoz zonani umuman bilmasligi kerak, u
//      faqat kalendar kunini yuboradi.
//
//   2. `dashboard` SANA PARAMETRINI UMUMAN OLMAYDI. U doim tenant
//      zonasidagi BUGUNNI qaytaradi va qaysi kun ekanini javobning o'zida
//      aytadi (`day` + `dayTimezone`). Ilgari bu yerda `traffic.uploadBytes`
//      degan mavjud bo'lmagan maydonlar o'qilardi — natijada Bosh sahifadagi
//      uchala KPI jimgina "—" ko'rsatardi (zod `.nullish()` bo'lgani uchun
//      xato ham chiqmasdi).
//
// ⚠️ OYNA CHEGARASI 366 KUN — serverda majburlanadi
// (`ReportWindow.MAX_DAYS`), uzunroq so'rov 400 bo'ladi. UI shuning uchun
// tanlovni oldindan cheklaydi (`ReportsPage`), foydalanuvchi xatoga
// urilmasin.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { moneySchema, toMoney } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import { downloadServerExport } from '@/shared/lib/serverExport'

/**
 * Hisobot oynasi — TENANT vaqt zonasidagi kalendar kunlari, `YYYY-MM-DD`.
 * Ikkala chegara ham KIRADI (server ichkarida yarim ochiq oraliqqa o'giradi).
 */
export interface ReportRange {
  from: string
  to: string
}

/** Qadam yirikligi — serverdagi `Granularity` enum'i bilan bir xil */
export const GRANULARITIES = ['DAY', 'MONTH'] as const
export type Granularity = (typeof GRANULARITIES)[number]

/** Serverdagi `ReportWindow.MAX_DAYS` — undan uzun oyna 400 qaytaradi */
export const MAX_RANGE_DAYS = 366

/** "Top" ro'yxatlaridagi eng ko'p qator — `ReportLimits.MAX_TOP` */
export const MAX_TOP_ROWS = 100

function rangeParams(range: ReportRange): Record<string, string> {
  return { from: range.from, to: range.to }
}

// --- Dashboard xulosasi ----------------------------------------------------

const dashboardSchema = z.object({
  /** Hisobot QAYSI kun uchun — tenant zonasida hisoblangan */
  day: z.string(),
  /** Kun chegarasi qaysi zonada hisoblangani, masalan "Asia/Kabul" */
  dayTimezone: z.string(),
  /**
   * ⚠️ HAMMASI `.nullish()` — `null` "SIZGA KO'RINMAYDI" degani, `0` emas.
   *
   * Backend ilgari doira qamramagan metrikani 0 qilib qaytarardi va AGENT
   * roli bilan kirgan odam "Jami abonentlar 0, Bugungi daromad 0 AFN" ni
   * ko'rib tashkilotda hech nima yo'q deb o'ylardi (`DashboardSummary`
   * javadoc'i). Endi u `null` yuboradi va panel chiziqcha chizadi.
   */
  onlineSubscribers: z.number().nullish(),
  totalSubscribers: z.number().nullish(),
  todayUploadBytes: z.number().nullish(),
  todayDownloadBytes: z.number().nullish(),
  todayTotalBytes: z.number().nullish(),
  todayRevenue: moneySchema.nullish(),
})

export interface DashboardReport {
  /** Tenant zonasidagi kun (`YYYY-MM-DD`) — oyna shu kunga bog'lanadi */
  day: string
  dayTimezone: string
  /** `null` — ko'rinish doirangiz bu ko'rsatkichni qamramaydi */
  onlineSubscribers: number | null
  totalSubscribers: number | null
  /** Abonent YUBORGAN bayt. `null` — doiradan tashqari */
  uploadBytes: number | null
  /** Abonent OLGAN bayt. `null` — doiradan tashqari */
  downloadBytes: number | null
  totalBytes: number | null
  /** `null` — daromad tenant ko'rsatkichi, sizning doirangizga kirmaydi */
  revenue: Money | null
}

/**
 * Bugungi xulosa. Sana parametri YO'Q — "bugun" ni server tenant zonasida
 * o'zi aniqlaydi va javobda `day`/`dayTimezone` bilan tasdiqlaydi.
 */
export async function fetchDashboardReport(): Promise<DashboardReport> {
  const response = await api.get<unknown>('/api/reports/dashboard')
  const raw = dashboardSchema.parse(response.data)
  return {
    day: raw.day,
    dayTimezone: raw.dayTimezone,
    // ⚠️ `?? 0` QO'YMANG — sabab sxema ustidagi izohda
    onlineSubscribers: raw.onlineSubscribers ?? null,
    totalSubscribers: raw.totalSubscribers ?? null,
    uploadBytes: raw.todayUploadBytes ?? null,
    downloadBytes: raw.todayDownloadBytes ?? null,
    totalBytes: raw.todayTotalBytes ?? null,
    revenue: raw.todayRevenue ? toMoney(raw.todayRevenue) : null,
  }
}

// --- Trafik ----------------------------------------------------------------

/**
 * ⚠️ `label` SERVERDA tayyorlanadi (`YYYY-MM-DD` yoki `YYYY-MM`) va u
 * TENANT zonasidagi davrni bildiradi. Uni brauzerda qayta formatlash
 * (`new Date(label)`) kunni bir kunga surib yuborardi — aynan shuning
 * uchun serverda tayyor holda beriladi (`TrafficPoint` javadoc'i).
 */
const trafficPointSchema = z.object({
  periodStart: z.string(),
  label: z.string(),
  uploadBytes: z.number(),
  downloadBytes: z.number(),
  totalBytes: z.number(),
  subscriberCount: z.number(),
  freeBytes: z.number().default(0),
})

export type TrafficPoint = z.infer<typeof trafficPointSchema>

export async function fetchTrafficReport(
  range: ReportRange,
  granularity: Granularity,
): Promise<TrafficPoint[]> {
  const response = await api.get<unknown>('/api/reports/traffic', {
    params: { ...rangeParams(range), granularity },
  })
  return z.array(trafficPointSchema).parse(response.data)
}

/** Bitta abonentning trafigi. Doiradan tashqarida bo'lsa BO'SH ro'yxat (403 emas). */
export async function fetchSubscriberTrafficReport(
  subscriberId: string,
  range: ReportRange,
  granularity: Granularity,
): Promise<TrafficPoint[]> {
  const response = await api.get<unknown>(
    `/api/reports/traffic/${encodeURIComponent(subscriberId)}`,
    { params: { ...rangeParams(range), granularity } },
  )
  return z.array(trafficPointSchema).parse(response.data)
}

// --- Top iste'molchilar ----------------------------------------------------

const topConsumerSchema = z.object({
  /** Kartochkaga o'tish uchun — login o'zgarishi mumkin, id o'zgarmaydi */
  subscriberId: z.string(),
  login: z.string(),
  uploadBytes: z.number(),
  downloadBytes: z.number(),
  totalBytes: z.number(),
  freeBytes: z.number().default(0),
})

export type TopConsumer = z.infer<typeof topConsumerSchema>

export async function fetchTopConsumersReport(
  range: ReportRange,
  limit: number,
): Promise<TopConsumer[]> {
  const response = await api.get<unknown>('/api/reports/top-consumers', {
    params: { ...rangeParams(range), limit: Math.min(limit, MAX_TOP_ROWS) },
  })
  return z.array(topConsumerSchema).parse(response.data)
}

// --- Daromad ---------------------------------------------------------------

/**
 * ⚠️ Bu FAQAT TAN OLINGAN SOTUV: `REVENUE` hisoblariga tushgan kredit.
 * Balans to'ldirish (`TOPUP`) va pulsiz tuzatish (`WRITE_OFF`) KIRMAYDI —
 * aks holda bir pul ikki marta sanalardi (`RevenuePoint` javadoc'i,
 * `docs/02-LEDGER.md` §7).
 *
 * Daromad butun tenantniki, shuning uchun reseller uni ko'rmaydi: javob
 * bo'sh ro'yxat bo'ladi (403 emas). UI buni BO'SH HOLAT sifatida
 * ko'rsatishi kerak, xato sifatida emas.
 */
const revenuePointSchema = z.object({
  periodStart: z.string(),
  label: z.string(),
  revenue: moneySchema,
  txnCount: z.number(),
})

export interface RevenuePoint {
  periodStart: string
  label: string
  revenue: Money
  txnCount: number
}

export async function fetchRevenueReport(
  range: ReportRange,
  granularity: Granularity,
): Promise<RevenuePoint[]> {
  const response = await api.get<unknown>('/api/reports/revenue', {
    params: { ...rangeParams(range), granularity },
  })
  return z
    .array(revenuePointSchema)
    .parse(response.data)
    .map((point) => ({
      periodStart: point.periodStart,
      label: point.label,
      revenue: toMoney(point.revenue),
      txnCount: point.txnCount,
    }))
}

// --- Daromad KESIMLARDA -----------------------------------------------------
//
// `GET /api/reports/revenue/by/{dimension}` — mijoz hujjatining §5.1
// bandlari «filial kesimida», «agent kesimida», «mijoz kesimida»,
// «paket kesimida» AYNI so'rovdan chiqadi (`RevenueDimension` javadoc'i:
// daromadning TA'RIFI to'rtalasida bir xil, farq faqat guruhlash
// ustunida).
//
// ⚠️ Bu ma'lumot panelda ilgari FAQAT EKSPORT sifatida ochilardi
// (`REPORT_EXPORT_KINDS` da `revenue-by`), ya'ni rahbar «qaysi paket
// qancha pul keltirdi» degan savolga javob olish uchun faylni yuklab
// olishga majbur edi.
//
// ⚠️ `total` QATORLARDAN QO'SHILMAYDI — u mustaqil so'rov, ayni doira
// bilan (`RevenueBreakdown` javadoc'i). Panel uni qayta hisoblamaydi:
// ikki hisobning mos kelishi o'z-o'zini tekshiruv, va qatorlar
// `truncated` bilan kesilgan bo'lishi mumkin.
//
// ⚠️ `unattributed` — HISOBOTNING ENG MUHIM QISMI va u ALOHIDA
// ko'rsatiladi. Zanjir (txn → abonent → agent → xarajat markazi) har
// bo'g'inida uzilishi mumkin; uzilgan qator YASHIRILSA rahbar
// «filiallarimiz sotmayapti» degan XATO xulosaga kelardi. Aslida savol
// boshqa: daromad KIMGA tegishli ekani yozilmagan.

/**
 * Kesim qatori.
 *
 * ⚠️ `bucketKey`/`bucketLabel` JAVOBDA UMUMAN BO'LMASLIGI mumkin —
 * o'lchandi (Staging, 2026-08-14): taqsimlanmagan qatorda Jackson
 * `null` maydonlarni tashlab yuboradi va JSON shunchaki
 * `{"revenue":…,"txnCount":171}` bo'lib keladi. Shuning uchun
 * `.nullish()`: `z.string()` bo'lsa butun hisobot parse bosqichida
 * yiqilardi.
 *
 * ⚠️ `bucketLabel` bo'sh bo'lsa panel «Taqsimlanmagan» deb yozadi,
 * BO'SH KATAK emas (`RevenueBreakdown.Row` javadoc'i shuni talab
 * qiladi).
 */
const revenueBucketSchema = z.object({
  bucketKey: z.string().nullish(),
  bucketLabel: z.string().nullish(),
  revenue: moneySchema,
  txnCount: z.number(),
})

/**
 * ⚠️ `dimension` — ERKIN SATR, `z.enum` EMAS. Backend yangi kesim
 * qo'shsa (`SERVICE` uchun manba paydo bo'lganda — `docs/11` §5.6-A),
 * `z.enum` butun sahifani o'chirib qo'yardi (`QARORLAR.md` §30.10:
 * «z.enum ni qisqartirish» qatori).
 *
 * ⚠️ Eksport qilinadi — `tests/reports-management.mjs` uni JONLI javob
 * shakliga qarshi ishlatadi (backend maydon driftini ushlash uchun).
 */
export const revenueBreakdownSchema = z.object({
  dimension: z.string(),
  rows: z.array(revenueBucketSchema).default([]),
  total: moneySchema,
  unattributed: moneySchema,
  scopeRestricted: z.boolean().default(false),
  truncated: z.boolean().default(false),
})

export interface RevenueBucket {
  /** `null` — zanjir uzilgan, qator «Taqsimlanmagan» savatida */
  bucketKey: string | null
  bucketLabel: string | null
  revenue: Money
  txnCount: number
}

export interface RevenueBreakdown {
  dimension: string
  rows: readonly RevenueBucket[]
  /** ⚠️ MUSTAQIL so'rovdan — qatorlardan qo'shilmaydi */
  total: Money
  /** Kesimga bog'lab bo'lmagan daromad — nolga aylantirilmaydi */
  unattributed: Money
  scopeRestricted: boolean
  truncated: boolean
}

export async function fetchRevenueBreakdown(
  dimension: RevenueDimension,
  range: ReportRange,
  signal?: AbortSignal,
): Promise<RevenueBreakdown> {
  const response = await api.get<unknown>(
    `/api/reports/revenue/by/${encodeURIComponent(dimension)}`,
    { ...(signal ? { signal } : {}), params: rangeParams(range) },
  )
  const raw = revenueBreakdownSchema.parse(response.data)
  return {
    dimension: raw.dimension,
    rows: raw.rows.map((row) => ({
      bucketKey: row.bucketKey ?? null,
      bucketLabel: row.bucketLabel ?? null,
      revenue: toMoney(row.revenue),
      txnCount: row.txnCount,
    })),
    total: toMoney(raw.total),
    unattributed: toMoney(raw.unattributed),
    scopeRestricted: raw.scopeRestricted,
    truncated: raw.truncated,
  }
}

// --- Abonent bazasining harakati (o'sish va churn) --------------------------
//
// `GET /api/reports/subscriber-movement` — mijoz hujjatining §5.5
// «abonent bazasi o'sishi» va «ketish darajasi» bandlari. Ular alohida
// hisobot EMAS: ikkalasi ham `subscriber.subscriber` ning AYNI ikki
// ustunidan (`created_at`, `closed_at`) chiqadi.
//
// ⚠️ Bu ham panelda faqat EKSPORT sifatida bor edi.
//
// ⚠️ TENGLIK: `openingBase + joined − closed = closingBase`. Panel uni
// QAYTA HISOBLAMAYDI, faqat KO'RSATADI — buzilsa u serverdagi kun
// chegarasi yoki doira xatosi va uni yashirmaslik kerak.

const movementRowSchema = z.object({
  periodStart: z.string(),
  /** ⚠️ SERVERDA tayyorlanadi, tenant zonasida — brauzerda qayta emas */
  label: z.string(),
  joined: z.number(),
  closed: z.number(),
  netChange: z.number(),
})

/**
 * ⚠️ `churnPercent` — `null` bo'lishi ODATIY va u NOL EMAS: oyna
 * boshidagi baza nol bo'lsa bo'linuvchi yo'q, ya'ni savol umuman
 * qo'yilmagan (`SubscriberMovement#churnPercent` javadoc'i). Jonli
 * Staging'da (2026-08-14) aynan shunday — maydon javobda umuman
 * kelmaydi, shuning uchun `.nullish()`.
 *
 * ⚠️ Eksport qilinadi — jonli javob shakliga qarshi ishlatiladi.
 */
export const subscriberMovementSchema = z.object({
  granularity: z.string(),
  rows: z.array(movementRowSchema).default([]),
  openingBase: z.number(),
  closingBase: z.number(),
  /** ⚠️ MUSTAQIL hisob — qatorlardan qo'shilmaydi */
  joined: z.number(),
  closed: z.number(),
  netChange: z.number(),
  churnPercent: z.number().nullish(),
  scopeRestricted: z.boolean().default(false),
})

export type SubscriberMovementRow = z.infer<typeof movementRowSchema>

export interface SubscriberMovement {
  granularity: string
  rows: readonly SubscriberMovementRow[]
  openingBase: number
  closingBase: number
  joined: number
  closed: number
  netChange: number
  /** `null` — oyna boshida baza nol, «0%» YOLG'ON bo'lardi */
  churnPercent: number | null
  scopeRestricted: boolean
}

export async function fetchSubscriberMovement(
  range: ReportRange,
  granularity: Granularity,
  signal?: AbortSignal,
): Promise<SubscriberMovement> {
  const response = await api.get<unknown>('/api/reports/subscriber-movement', {
    ...(signal ? { signal } : {}),
    params: { ...rangeParams(range), granularity },
  })
  const raw = subscriberMovementSchema.parse(response.data)
  return {
    granularity: raw.granularity,
    rows: raw.rows,
    openingBase: raw.openingBase,
    closingBase: raw.closingBase,
    joined: raw.joined,
    closed: raw.closed,
    netChange: raw.netChange,
    // ⚠️ `?? 0` QO'YMANG — `null` «churn 0%» degani EMAS
    churnPercent: raw.churnPercent ?? null,
    scopeRestricted: raw.scopeRestricted,
  }
}

// --- Agent natijalari ------------------------------------------------------

/**
 * ⚠️ `sold` va `commission` — OYNA ichidagi yig'indi, `balance` esa hisobning
 * JORIY qoldig'i va oynaga umuman bog'liq emas. Ularni bitta jadvalda
 * ko'rsatganda buni ustun sarlavhasida aytish shart, aks holda operator
 * qoldiqni ham "davr bo'yicha" deb o'qiydi.
 *
 * Qaysi qoldiq ko'rsatilishi `settlementMode` ga bog'liq: `DEPOSIT` →
 * agent depoziti, `POSTPAID` → topshirilmagan naqd.
 */
const agentPerformanceSchema = z.object({
  agentId: z.string(),
  code: z.string(),
  displayName: z.string(),
  settlementMode: z.string(),
  sold: moneySchema,
  commission: moneySchema,
  balance: moneySchema,
  saleCount: z.number(),
})

export interface AgentPerformance {
  agentId: string
  code: string
  displayName: string
  settlementMode: string
  sold: Money
  commission: Money
  balance: Money
  saleCount: number
}

export async function fetchAgentsReport(
  range: ReportRange,
  limit: number,
): Promise<AgentPerformance[]> {
  const response = await api.get<unknown>('/api/reports/agents', {
    params: { ...rangeParams(range), limit: Math.min(limit, MAX_TOP_ROWS) },
  })
  return z
    .array(agentPerformanceSchema)
    .parse(response.data)
    .map((row) => ({
      agentId: row.agentId,
      code: row.code,
      displayName: row.displayName,
      settlementMode: row.settlementMode,
      sold: toMoney(row.sold),
      commission: toMoney(row.commission),
      balance: toMoney(row.balance),
      saleCount: row.saleCount,
    }))
}

// --- Qurilmalar (joylashuv reyestri) ---------------------------------------

/**
 * ⚠️ BU HISOBOT SANA OYNASINI OLMAYDI va bu ataylab: u trafik emas, JORIY
 * HOLATNI sanaydi ("hozir nechta qurilma bor va ular qayerda"). Unga davr
 * berish "o'tgan oyda nechta router bor edi" degan javobsiz savolni
 * tug'dirardi — inventarda o'tmish holati saqlanmaydi.
 *
 * `GET /api/reports/devices` → `report.read` huquqi ostida, qolgan hisobotlar
 * bilan bir xil.
 */
const deviceRegionSchema = z.object({
  region: z.string(),
  siteCount: z.number(),
  nasCount: z.number(),
  equipmentCount: z.number(),
  /** `nasCount + equipmentCount` — serverda hisoblanadi, panelda qayta emas */
  deviceCount: z.number(),
})

/**
 * Kesim qatori — `bucket` ERKIN SATR, enum EMAS.
 *
 * Server yangi jihoz turi yoki holat qo'shsa, panel uni TANIMAGAN holda ham
 * ko'rsatishi kerak: `z.enum(...)` bo'lsa butun hisobot zod xatosi bilan
 * yiqilardi. Tarjimasi topilmagan kalit o'z holicha chiziladi
 * (`t(..., {defaultValue: bucket})`).
 *
 * ⚠️ MAYDON NOMI `key` EMAS, `bucket` — VA BU YERDA XATO BOR EDI.
 *
 * O'lchov (2026-08-13, staging `GET /api/reports/devices`):
 * <pre>
 *   "byType":  [{ "bucket": "ONU",      "count": 1 }]
 *   "byState": [{ "bucket": "IN_STOCK", "count": 1 }]
 * </pre>
 *
 * Sxemada esa `key` yozilgan edi va zod butun hisobotni rad etardi:
 * `ZodError: byType[0].key expected string, received undefined`. Ya'ni
 * Hisobotlar sahifasining «Qurilmalar» bo'limi konsolga xato yozib,
 * ekranda buzuq turardi — nuqsonni tashqi tekshiruvchi topdi.
 *
 * ⚠️ Bu NOM O'ZGARISHI, ma'lumot yo'qligi emas: backend rekordi
 * `report.device.dto.CountRow` maydonni ATAYLAB `bucket` deb ataydi va
 * javadoc'ida sababini yozadi. Ya'ni to'g'ri tomon — backend, tuzatish
 * esa panelda. Backendga tegilmadi.
 */
const deviceBreakdownSchema = z.object({ bucket: z.string(), count: z.number() })

/**
 * Joylashuvi ko'rsatilmagan qurilma — `Unplaced.nas` / `.equipment` elementi.
 *
 * ⚠️ MAYDONLAR ERKIN SATR: `deviceId` NAS uchun raqamli id, jihoz uchun
 * UUID bo'lishi mumkin. Panel ularni faqat ko'rsatadi, hech qanday
 * so'rovga qo'ymaydi.
 */
const unplacedDeviceSchema = z.object({
  deviceId: z.string(),
  label: z.string().nullish(),
  detail: z.string().nullish(),
})

/**
 * ⚠️ BU SXEMA ESKIRGAN EDI VA PANEL BUZILGANDI.
 *
 * Bu yerda `{nas: number, equipment: number}` yozilgan edi. Backend esa
 * (`report.device.dto.Unplaced`) allaqachon RO'YXAT qaytarardi va sanoqni
 * alohida maydonlarda (`nasCount`/`equipmentCount`) berardi — chunki
 * ro'yxat `LIMIT` bilan kesilgan bo'lishi mumkin va uzunlik to'liq sonni
 * ANIQ ko'rsatmaydi.
 *
 * Natijada `zod` butun hisobotni rad etardi va Hisobotlar sahifasining
 * "Qurilmalar" bo'limida foydalanuvchiga XOM zod xatosi ko'rinardi
 * (`[{"code":"invalid_type",…}]`). Ya'ni bo'lim ochiq holda buzuq turardi.
 *
 * ⚠️ Sanoq `nas.length` DAN OLINMAYDI: ro'yxat kesilgan bo'lsa uzunlik
 * to'liqlik darajasini haqiqiydan yaxshiroq ko'rsatardi (`Unplaced`
 * javadoc'i).
 */
const unplacedSchema = z.object({
  nas: z.array(unplacedDeviceSchema),
  equipment: z.array(unplacedDeviceSchema),
  nasCount: z.number(),
  equipmentCount: z.number(),
})

const deviceReportSchema = z.object({
  byRegion: z.array(deviceRegionSchema),
  byType: z.array(deviceBreakdownSchema),
  byState: z.array(deviceBreakdownSchema),
  /**
   * ⚠️ ENG MUHIM RAQAM: joylashuvi ko'rsatilmagan qurilmalar. Bu hisobotning
   * qolgan qismi qanchalik ishonchli ekanini aytadi — viloyatlar kesimi
   * aynan shu qurilmalarni HISOBGA OLMAYDI. Shuning uchun UI'da u izoh
   * emas, ko'rinadigan ko'rsatkich bo'lishi kerak.
   */
  unplaced: unplacedSchema,
})

export type DeviceRegionRow = z.infer<typeof deviceRegionSchema>
export type DeviceBreakdownRow = z.infer<typeof deviceBreakdownSchema>
export type DeviceReport = z.infer<typeof deviceReportSchema>

export async function fetchDeviceReport(): Promise<DeviceReport> {
  const response = await api.get<unknown>('/api/reports/devices')
  return deviceReportSchema.parse(response.data)
}

// ---------------------------------------------------------------------------
// EKSPORT — `report.export`
//
// ⚠️ NEGA SERVERDA, brauzerda emas.
//
// Ilgari `ReportsPage` CSV'ni brauzerda, ALLAQACHON YUKLANGAN sahifadan
// yasardi (`shared/lib/download.ts`). Ikki oqibati bor edi:
//
//   1) `report.export` huquqi amalda HECH NARSANI boshqarmasdi. Tugma
//      yashirilardi, lekin ma'lumot allaqachon brauzerda edi — ya'ni
//      `report.read` i bor har kim uni oddiy nusxa-ko'chirish bilan ham
//      olardi. Huquq katalogda bor, majburlanmagan holatda edi.
//   2) Eksport EKRANDAGI qator bilan cheklanardi, ya'ni "yillik hisobotni
//      Excel'ga chiqar" degan asosiy stsenariy ishlamasdi.
//
// Endi fayl serverda yasaladi (`GET /api/reports/export/{kind}`,
// `ReportExportResource`) va u `@RequiresPermission(REPORT_EXPORT)` ostida.
//
// ⚠️ `responseType: 'blob'` — javob interceptor'i `isEnvelope(body)` ni
// Blob ustida chaqiradi va u zararsiz (Blob'da na `success`, na `data`
// bor), ya'ni javob o'zgarmasdan o'tadi. XATO tanasi esa Blob bo'lib
// keladi va uni ochish SHART — `serverExport.unwrapBlobError` javadoc'i.
//
// ⚠️ FORMAT ENDI TANLANADI. Ilgari panel doim CSV so'rardi, holbuki
// `xsib-common/export` XLSX va JSON ni ham AYNI kod bilan beradi.
// Ya'ni Excel fayli mavjud bo'la turib operatorga yetib bormasdi.
// ---------------------------------------------------------------------------

/**
 * Backend `ReportKind.slug()` bilan AYNAN bir xil qiymatlar.
 *
 * ⚠️ `revenue-by` va `subscribers` 2026-08-14 da QO'SHILDI — ular
 * backendda allaqachon bor edi, panel esa faqat beshtasini so'rardi.
 * `subscribers` — ABONENTLAR REYESTRI, ya'ni bu yerdagi yagona
 * chinakam katta ro'yxat (server uni 5 000 talik sahifalar bilan
 * o'qiydi). Moslikni `tests/export.mjs` §1 qo'riqlaydi.
 *
 * ⚠️ `subscriber-movement` — o'sha kuni backendga qo'shilgan (abonent
 * bazasining harakati: qo'shilgan, ketgan, sof o'sish, churn %).
 * Qorovul uni DARHOL ushladi: ikki agent ikki tomonni yozdi va oraliq
 * holatda ro'yxat mos kelmay qoldi. Aynan shuning uchun qorovul bor —
 * usiz backendda hisobot bo'lardi-yu, ekranda hech qachon ko'rinmasdi.
 */
export const REPORT_EXPORT_KINDS = [
  'traffic',
  'revenue',
  'revenue-by',
  'top-consumers',
  'agents',
  'devices',
  'subscribers',
  'subscriber-movement',
] as const

export type ReportExportKind = (typeof REPORT_EXPORT_KINDS)[number]

/**
 * `revenue-by` kesimi — serverdagi `RevenueDimension` enum'i.
 *
 * ⚠️ `PLAN` (paket kesimi) backendda bor edi va panel ro'yxatidan
 * TUSHIB QOLGAN edi — ya'ni "qaysi paket qancha pul keltirdi" degan
 * savolga javob beradigan kesim ekranda umuman ko'rinmasdi.
 * `tests/export.mjs` §1 endi ro'yxatni Java enum'i bilan solishtiradi.
 */
export const REVENUE_DIMENSIONS = ['COST_CENTER', 'AGENT', 'SUBSCRIBER', 'PLAN'] as const
export type RevenueDimension = (typeof REVENUE_DIMENSIONS)[number]

export interface ReportExportParams {
  /** `YYYY-MM-DD`, tenant zonasidagi kun — `/api/reports/*` bilan bir xil */
  from?: string
  to?: string
  granularity?: Granularity
  limit?: number
  /** `revenue-by` uchun kesim */
  dimension?: RevenueDimension
  /** `subscribers` uchun holat filtri, masalan `ACTIVE` */
  state?: string | null
  /** `subscribers` uchun tur filtri, masalan `PPPOE` */
  subscriberKind?: string | null
  onProgress?: (loadedBytes: number) => void
}

/**
 * Hisobotni fayl sifatida yuklab oladi.
 *
 * Fayl nomi `Content-Disposition` dan olinadi — davr va tur serverda
 * bir marta hal qilinadi, ya'ni panel va boshqa mijozlar bir xil nom
 * oladi. Sarlavha yetib kelmasa (proxy uni kesib tashlashi mumkin)
 * zaxira nom yasaladi.
 *
 * ⚠️ `subscribers` da oyna `created_at` ga qo'llanadi — "shu davrda
 * RO'YXATGA OLINGANLAR", "shu davrda faol bo'lganlar" EMAS
 * (`ReportExportResource` javadoc'i). Ekranda ham aynan shunday
 * yozilishi kerak, aks holda operator faylni ro'yxatning to'liq nusxasi
 * deb o'qirdi.
 */
export async function downloadReportExport(
  kind: ReportExportKind,
  format: string,
  params: ReportExportParams,
): Promise<void> {
  await downloadServerExport({
    path: `/api/reports/export/${kind}`,
    format,
    params: {
      from: params.from,
      to: params.to,
      granularity: params.granularity,
      limit: params.limit,
      dimension: params.dimension,
      state: params.state,
      subscriberKind: params.subscriberKind,
    },
    fallbackBaseName: params.from && params.to ? `${kind}-${params.from}_${params.to}` : kind,
    ...(params.onProgress ? { onProgress: params.onProgress } : {}),
  })
}
